import React, { useEffect, useMemo, useState } from 'react';
import { useT } from '../i18n';
import { formatTonExact } from '../lib/ton-format';
import {
    isVisuallyExpired, remainingTurnoverNano, secondsUntilExpiry, wagerProgressPercent,
    type BonusView,
} from '../lib/bonus-state';
import { BONUS_AVAILABLE_IN_BUILD } from '../lib/bonus-availability';
// The visibility predicate lives with the owner that produces both latches, so the panel cannot
// drift from it — a second hand-written copy of `bootstrapped || initialReadExhausted` here is
// exactly how the two would silently disagree.
import { isVisible } from '../lib/bonus-read-coordinator.ts';

/**
 * CF-BONUS Ticket B — player-facing bonus surfaces.
 *
 * Every contract decision already lives in `lib/bonus-state.ts`; this file only renders a
 * `BonusView`. It deliberately holds NO parsing, NO eligibility inference and NO campaign
 * financials — the component cannot know anything the endpoint did not say.
 *
 * The three Scorer amendments that bite here:
 *
 *  A1 — `unavailable` renders its OWN copy. It must never fall through to the no-bonus branch
 *       or render nothing. A player holding chips who sees an empty space concludes their money
 *       is gone. Silence is not a safe default when there is money at stake.
 *  A2 — the countdown MAY flip an active bonus to "expired" once the clock passes `expires_at`,
 *       but it must not invent a terminal timestamp or a conversion outcome. We show the expiry
 *       state; we never claim what the sweeper will decide.
 *  A3 — nano amounts are formatted from BigInt via formatTonExact. Nothing here puts a balance
 *       through JS `Number`: two values one nano apart, both above 2^53, collapse to the same
 *       Number, and a player would be told they had finished wagering when they had not.
 */

const GOLD = 'var(--gold)';

/** Countdown, ticking at 30s. Seven-day expiry does not need per-second precision. */
function useNowSeconds(enabled: boolean): number {
    const [now, setNow] = useState(() => Math.floor(Date.now() / 1000));
    useEffect(() => {
        if (!enabled) return;
        const id = setInterval(() => setNow(Math.floor(Date.now() / 1000)), 30_000);
        return () => clearInterval(id);
    }, [enabled]);
    return now;
}

/** Humanised remaining time using the locale's own units. Never negative. */
function useExpiryLabel(expiresAt: number, nowSeconds: number): string {
    const t = useT();
    const left = secondsUntilExpiry(expiresAt, nowSeconds);
    const days = Math.floor(left / 86400);
    const hours = Math.floor((left % 86400) / 3600);
    const mins = Math.floor((left % 3600) / 60);
    if (days > 0) return t('bonus.time.days_hours', { days, hours });
    if (hours > 0) return t('bonus.time.hours_mins', { hours, mins });
    return t('bonus.time.mins', { mins });
}

function Card({ children }: { children: React.ReactNode }) {
    return (
        <div
            style={{
                border: '1px solid rgba(212,175,55,0.35)',
                borderRadius: 12,
                padding: '14px 16px',
                background: 'rgba(212,175,55,0.06)',
                lineHeight: 1.6,
                fontSize: 14,
            }}
        >
            {children}
        </div>
    );
}

function Title({ children }: { children: React.ReactNode }) {
    return <div style={{ color: GOLD, fontWeight: 600, marginBottom: 6 }}>{children}</div>;
}

/** The 10x wagering bar. `percent` is already clamped 0..100 and computed in BigInt. */
function ProgressBar({ percent }: { percent: number }) {
    return (
        <div
            role="progressbar"
            aria-valuenow={percent}
            aria-valuemin={0}
            aria-valuemax={100}
            style={{
                height: 8, borderRadius: 4, background: 'rgba(255,255,255,0.12)',
                overflow: 'hidden', margin: '6px 0',
            }}
        >
            <div style={{ width: `${percent}%`, height: '100%', background: GOLD }} />
        </div>
    );
}

export interface BonusPanelProps {
    view: BonusView;
    /**
     * CF-BONUS-FLASH-FIX-V1: whether the owner has EVER received a real server answer.
     * `unavailable` before that means "still establishing" and renders NOTHING — showing an
     * error card during app boot (or while the programme is not yet enabled server-side) told
     * every player the software was broken (PL #12350). `unavailable` after a real answer is a
     * genuine mid-session read failure and keeps the honest A1 card.
     */
    bootstrapped: boolean;
    /**
     * CF-BONUS-SETTLED-UNAVAILABLE-RESILIENCE-V1: the owner has spent its establishment budget
     * without ever getting an answer, and will not keep trying on its own.
     *
     * ⛔ This is the second half of the visibility predicate and it is NOT optional. With
     * `bootstrapped` alone, a player whose very first read never succeeds sees NOTHING, forever —
     * indistinguishable from "you have no bonus". `visible := bootstrapped || initialReadExhausted`
     * is what turns a permanent unknown into an honest "we can't show this right now".
     */
    initialReadExhausted: boolean;
    /** Link target for the full terms. Omitted → no link rendered. */
    termsHref?: string;
}

/**
 * The status surface. Renders for active + terminal + unavailable.
 * Returns null ONLY for `none` — a server-confirmed absence of any bonus, which is the one
 * case where showing nothing is honest. The teaser is a separate component (see BonusTeaser).
 */
export function BonusPanel({ view, bootstrapped, initialReadExhausted, termsHref }: BonusPanelProps) {
    const t = useT();
    const now = useNowSeconds(view.kind === 'active');

    // A2: the local clock may present an active bonus as expired, without fabricating terminal
    // state. Computed before the branch so the hook order never changes between renders.
    const visuallyExpired = isVisuallyExpired(view, now);

    const expiresAt = view.kind === 'active' ? view.expiresAt : 0;
    const expiryLabel = useExpiryLabel(expiresAt, now);

    const progress = useMemo(() => {
        if (view.kind !== 'active') return { percent: 0, done: '0', target: '0', remaining: '0' };
        return {
            percent: wagerProgressPercent(view.turnoverNano, view.wrTargetNano),
            done: formatTonExact(BigInt(view.turnoverNano)),
            target: formatTonExact(BigInt(view.wrTargetNano)),
            remaining: formatTonExact(BigInt(remainingTurnoverNano(view.turnoverNano, view.wrTargetNano))),
        };
    }, [view]);

    // Build-variant participation gate. Checked AFTER the hooks so hook order is stable, and
    // before ANY branch — including `unavailable`, which is still a bonus surface.
    if (!BONUS_AVAILABLE_IN_BUILD) return null;

    // ── A1: our ignorance, rendered as ignorance — but ONLY once ignorance is news. ──
    // Before the first real server answer, `unavailable` is the STARTING state (and the
    // permanent state while the backend keeps the programme disabled): rendering it as an
    // error card put a flashing "temporarily unavailable" in front of every player on every
    // open (PL #12350). Nothing is the honest render — it asserts nothing, exactly like A1
    // demands, without dressing boot-up as an outage. After a real answer, a read failure is
    // genuine news and the card stays.
    if (view.kind === 'unavailable') {
        // ⛔ THE DISJUNCTION IS THE FIX. This line used to read `if (!bootstrapped) return null;`,
        // which meant the exhaustion card could never render at all: a player whose first read
        // never succeeded stayed on `bootstrapped === false` permanently, so the branch returned
        // null every time and they saw silence where their bonus should be. Silence is exactly
        // what "you have no bonus" looks like.
        if (!isVisible({ bootstrapped, initialReadExhausted })) return null;
        return (
            <Card>
                <Title>{t('bonus.unavailable.title')}</Title>
                <div style={{ color: '#cbd2dd' }}>{t('bonus.unavailable.body')}</div>
            </Card>
        );
    }

    // Server-confirmed absence. The offer teaser is rendered elsewhere, gated separately.
    if (view.kind === 'none') return null;

    if (view.kind === 'terminal') {
        const converted = BigInt(view.convertedNano);
        const titleKey = `bonus.terminal.${view.state}_title`;
        // A converted bonus with a zero amount is a real outcome (chips lost before the cap).
        // Saying "0.00 TON converted" is worse than saying it closed without a conversion.
        const body = view.state === 'converted'
            ? (converted > 0n
                ? t('bonus.terminal.converted_body', { amount: formatTonExact(converted) })
                : t('bonus.terminal.converted_zero'))
            : t(`bonus.terminal.${view.state}_body`);
        return (
            <Card>
                <Title>{t(titleKey)}</Title>
                <div style={{ color: '#cbd2dd' }}>{body}</div>
            </Card>
        );
    }

    // ── active ──
    const sticky = formatTonExact(BigInt(view.stickyNano));
    return (
        <Card>
            <Title>{t('bonus.active.title')}</Title>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <span style={{ color: '#cbd2dd' }}>{t('bonus.active.chips_label')}</span>
                <span style={{ color: GOLD, fontWeight: 600 }}>{sticky} TON</span>
            </div>
            <div style={{ color: '#8b93a1', fontSize: 12, marginBottom: 10 }}>
                {t('bonus.active.chips_note')}
            </div>

            <div style={{ color: '#cbd2dd' }}>{t('bonus.active.progress_label')}</div>
            <ProgressBar percent={progress.percent} />
            <div style={{ color: '#8b93a1', fontSize: 12 }}>
                {t('bonus.active.progress_detail', { done: progress.done, target: progress.target })}
            </div>
            <div style={{ color: '#cbd2dd', fontSize: 13, marginTop: 4 }}>
                {progress.percent >= 100
                    ? t('bonus.active.complete')
                    : t('bonus.active.remaining', { amount: progress.remaining })}
            </div>

            <div style={{ color: '#8b93a1', fontSize: 12, marginTop: 10 }}>
                {/* A2: past expires_at we say it expired — we do NOT claim what happens next. */}
                {visuallyExpired
                    ? t('bonus.active.expired_note')
                    : t('bonus.active.expires_in', { time: expiryLabel })}
            </div>

            {termsHref && (
                <a href={termsHref} style={{ color: GOLD, fontSize: 12, textDecoration: 'none' }}>
                    {t('bonus.teaser.terms_link')} →
                </a>
            )}
        </Card>
    );
}

export interface BonusTeaserProps {
    view: BonusView;
    termsHref?: string;
}

/**
 * The deposit-modal teaser.
 *
 * A5 — shown ONLY for a server-confirmed `none` WITH `offer_available: true`. It carries the
 * transaction-time qualification and must never imply that seeing it reserves a grant: the pot
 * can be consumed between render and deposit, and the caller can still fail the lifetime-first,
 * wallet-history, owner or solvency checks. A true "if you can see it, it's yours" would need an
 * atomic timed pre-reservation, which is a different fund-adjacent design and is NOT built.
 *
 * Every other view — including `unavailable` — renders nothing here. We never advertise an offer
 * we could not confirm, and we never guess eligibility client-side.
 */
export function BonusTeaser({ view, termsHref }: BonusTeaserProps) {
    const t = useT();
    if (!BONUS_AVAILABLE_IN_BUILD) return null;
    if (view.kind !== 'none' || !view.offerAvailable) return null;
    return (
        <Card>
            <Title>{t('bonus.teaser.headline')}</Title>
            <div style={{ color: '#cbd2dd' }}>{t('bonus.teaser.body')}</div>
            <div style={{ color: '#8b93a1', fontSize: 12, marginTop: 6 }}>
                {t('bonus.teaser.availability')}
            </div>
            {termsHref && (
                <a
                    href={termsHref}
                    style={{ color: GOLD, fontSize: 12, textDecoration: 'none', display: 'inline-block', marginTop: 8 }}
                >
                    {t('bonus.teaser.terms_link')} →
                </a>
            )}
        </Card>
    );
}
